/* Sk1ntro automatic cart recommendations. Plain JavaScript only. */
(() => {
  'use strict';
  const elementName = 'sk1ntro-cart-recommendations';
  if (customElements.get(elementName)) return;
  const cache = new Map();
  let adding = false;

  function root() {
    return (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  }

  function node(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }

  function variants(product) {
    return (product.variants || []).filter(v =>
      v.available && Number(v.price) > 0 && !v.requires_selling_plan
    );
  }

  function eligible(product, excluded) {
    const tags = Array.isArray(product.tags)
      ? product.tags.join('|') : String(product.tags || '');
    return product.available &&
      !excluded.has(String(product.id)) &&
      !product.gift_card && !product.requires_selling_plan &&
      !/gift[ -]?card/i.test(product.type || '') &&
      !/(^|[,|])\s*(free[ -]?gift|gift[ -]?with[ -]?purchase|gwp)\s*([,|]|$)/i.test(tags) &&
      variants(product).length > 0;
  }

  async function recommendations(id, currency, locale) {
    const key = [root(), currency, locale, id].join('|');
    const saved = cache.get(key);
    if (saved && saved.until > Date.now()) return saved.promise;
    if (cache.size >= 20) cache.delete(cache.keys().next().value);

    const promise = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);

      try {
        const url = root() + 'recommendations/products.json?' +
          new URLSearchParams({
            product_id: id,
            limit: '10',
            intent: 'related'
          });

        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error('Recommendations unavailable');
        }

        const data = await response.json();
        return Array.isArray(data.products) ? data.products : [];
      } finally {
        clearTimeout(timer);
      }
    })();

    cache.set(key, {
      until: Date.now() + 120000,
      promise
    });

    try {
      return await promise;
    } catch (error) {
      if (cache.get(key)?.promise === promise) cache.delete(key);
      throw error;
    }
  }

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.track = this.querySelector('[data-rec-track]');
      this.status = this.querySelector('[data-rec-status]');
      this.prev = this.querySelector('[data-rec-prev]');
      this.next = this.querySelector('[data-rec-next]');

      if (!this.track || !this.status || !this.prev || !this.next) {
        return;
      }

      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;

      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      this.prev.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);

      this.track.addEventListener('scroll', () => this.navigation(), {
        ...options,
        passive: true
      });

      if (window.ResizeObserver) {
        this.observer?.disconnect();
        this.observer = new ResizeObserver(() => this.navigation());
        this.observer.observe(this.track);
      }

      this.load(generation).catch(() => {
        if (generation === this.generation) this.hidden = true;
      });
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.events?.abort();
      this.observer?.disconnect();
    }

    async load(generation) {
      const excluded = new Set(
        JSON.parse(this.dataset.cartIds || '[]').map(String)
      );

      const seeds = [
        ...new Set(
          JSON.parse(this.dataset.seedIds || '[]').map(String)
        )
      ].filter(id => /^\d+$/.test(id)).slice(0, 2);

      if (!seeds.length) return;

      this.money = new Intl.NumberFormat(
        this.dataset.locale || 'en-CA',
        {
          style: 'currency',
          currency: this.dataset.currency || 'CAD'
        }
      );

      const groups = await Promise.allSettled(
        seeds.map(id =>
          recommendations(
            id,
            this.dataset.currency,
            this.dataset.locale
          )
        )
      );

      if (!this.isConnected || generation !== this.generation) return;

      const products = [];
      const lists = groups.map(result =>
        result.status === 'fulfilled' ? result.value : []
      );

      for (let index = 0; index < 10 && products.length < 6; index++) {
        for (const list of lists) {
          const product = list[index];

          if (product && eligible(product, excluded)) {
            products.push(product);
            excluded.add(String(product.id));

            if (products.length === 6) break;
          }
        }
      }

      this.track.replaceChildren(
        ...products.map(product => this.card(product))
      );

      this.hidden = !products.length;
      this.navigation();
    }

    moneyText(cents) {
      return this.money.format(Number(cents) / 100);
    }

    card(product) {
      const available = variants(product);
      let selected = available[0];

      const card = node('div', 'sk-cart-recs__card');
      card.setAttribute('role', 'listitem');

      const url = new URL(
        product.url || root() + 'products/' + product.handle,
        location.origin
      );

      if (url.origin !== location.origin) {
        throw new Error('Invalid product URL');
      }

      if (url.pathname.startsWith('/products/') && root() !== '/') {
        url.pathname = root() + url.pathname.slice(1);
      }

      const photoLink = node('a');
      const image = node('img', 'sk-cart-recs__image');

      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 320;
      image.height = 320;
      photoLink.append(image);

      const title = node(
        'a',
        'sk-cart-recs__title',
        product.title
      );

      const price = node('p', 'sk-cart-recs__price');
      const button = node('button', 'sk-cart-recs__add', 'Add');

      button.type = 'button';
      button.setAttribute(
        'aria-label',
        'Add ' + product.title + ' to cart'
      );

      card.append(photoLink, title, price);

      const update = () => {
        url.searchParams.set('variant', selected.id);
        photoLink.href = title.href = url.href;

        const src =
          selected.featured_image?.src ||
          selected.featured_image?.url ||
          (
            typeof product.featured_image === 'string'
              ? product.featured_image
              : product.featured_image?.src
          );

        if (src) {
          const imageURL = new URL(src, location.origin);

          if (/^https?:$/.test(imageURL.protocol)) {
            imageURL.searchParams.set('width', '320');
            image.src = imageURL.href;
          }
        }

        image.hidden = !src;
        price.replaceChildren();

        if (
          Number(selected.compare_at_price) >
          Number(selected.price)
        ) {
          price.append(
            node('s', '', this.moneyText(selected.compare_at_price))
          );
        }

        price.append(
          node('span', '', this.moneyText(selected.price))
        );
      };

      if ((product.variants || []).length > 1) {
        const select = node('select', 'sk-cart-recs__select');

        select.setAttribute(
          'aria-label',
          'Choose option for ' + product.title
        );

        available.forEach(variant => {
          const option = node('option', '', variant.title);
          option.value = variant.id;
          select.append(option);
        });

        select.addEventListener('change', () => {
          selected = available.find(
            v => String(v.id) === select.value
          );
          update();
        });

        card.append(select);
      }

      button.addEventListener(
        'click',
        () => this.add(selected.id, button)
      );

      card.append(button);
      update();
      return card;
    }

    async add(id, button) {
      if (adding || button.disabled) return;

      adding = true;
      button.disabled = true;
      button.textContent = 'Adding…';
      this.status.hidden = true;

      let confirmed = false;
      let uncertain = false;

      try {
        const response = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: Number(id), quantity: 1 }]
          })
        });

        let data;

        try {
          data = await response.json();
        } catch (_) {
          uncertain = response.ok;
          throw new Error(
            'Unable to confirm the result. Check your cart before trying again.'
          );
        }

        if (!response.ok || data.status) {
          uncertain = response.status >= 500;
          throw new Error(
            typeof data.description === 'string'
              ? data.description
              : 'This item could not be added. Please check its availability.'
          );
        }

        if (!Array.isArray(data.items) || !data.items.length) {
          uncertain = true;
          throw new Error(
            'Unable to confirm the result. Check your cart before trying again.'
          );
        }

        confirmed = true;
        button.textContent = 'Added';

        document.dispatchEvent(
          new CustomEvent('apps:product-added-to-cart')
        );
      } catch (error) {
        uncertain = uncertain || error instanceof TypeError;

        this.status.textContent = uncertain
          ? 'Unable to confirm the result. Check your cart before trying again.'
          : error.message;

        this.status.hidden = false;

        if (uncertain) {
          document.dispatchEvent(
            new CustomEvent('apps:product-added-to-cart')
          );
        }
      } finally {
        adding = false;

        if (!confirmed && !uncertain) {
          button.disabled = false;
          button.textContent = 'Add';
        } else if (uncertain) {
          button.textContent = 'Check cart';
        }
      }
    }

    move(direction) {
      this.track.scrollBy({
        left: direction * (this.track.clientWidth + 12),
        behavior: matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches ? 'auto' : 'smooth'
      });
    }

    navigation() {
      const maximum =
        this.track.scrollWidth - this.track.clientWidth;

      this.prev.disabled =
        maximum <= 1 || this.track.scrollLeft <= 1;

      this.next.disabled =
        maximum <= 1 || this.track.scrollLeft >= maximum - 1;
    }
  }

  customElements.define(elementName, CartRecommendations);
})();